var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { inject, injectable, Store } from '@servicetitan/react-ioc';
import { observable, action, makeObservable, toJS } from 'mobx';
import { LOG_CONFIG_TOKEN } from './log-config';
var EventType;
(function (EventType) {
    EventType["Delete"] = "delete";
})(EventType || (EventType = {}));
let LogStorage = class LogStorage extends Store {
    constructor(logConfig) {
        super();
        Object.defineProperty(this, "logConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: logConfig
        });
        Object.defineProperty(this, "items", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "maxLogSize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1000
        });
        Object.defineProperty(this, "version", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "nextItemId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "channel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        makeObservable(this);
        const version = window.parseInt(localStorage.getItem(this.storageVersionKey));
        if (version !== this.version) {
            localStorage.removeItem(this.storageKey);
            localStorage.setItem(this.storageVersionKey, String(this.version));
        }
        this.loadSavedItems();
        this.nextItemId = lastId(this.items) + 1;
        /*
         * Use the BroadcastChannel API to prevent different tabs from processing
         * the saved items. The first one to process the items sends a "delete" event
         * that causes all other instances to discard them from memory.
         */
        if ('BroadcastChannel' in globalThis) {
            this.channel = new BroadcastChannel(logConfig.storageKey);
            this.channel.addEventListener('message', ({ data }) => this.handleDeleteEvent(data));
        }
    }
    dispose() {
        var _a;
        (_a = this.channel) === null || _a === void 0 ? void 0 : _a.close();
    }
    get config() {
        return this.logConfig;
    }
    store(entry) {
        const item = {
            id: this.nextItemId++,
            type: entry.type,
            category: entry.category,
            code: entry.code,
            message: entry.message,
            data: JSON.stringify(entry.data),
            exception: this.stringifyError(entry.error),
        };
        let items = [...this.items, item];
        items = items.slice(-this.maxLogSize);
        this.setItems(items);
    }
    deleteToId(id) {
        const deletedItems = [];
        const items = this.items.filter(item => {
            if (item.id > id) {
                return true;
            }
            deletedItems.push(item);
            return false;
        });
        this.setItems(items);
        this.postDeleteEvent(deletedItems);
    }
    loadBatch(batchSize) {
        return this.items.slice(0, batchSize);
    }
    loadSavedItems() {
        var _a;
        try {
            const items = (_a = JSON.parse(localStorage.getItem(this.storageKey))) !== null && _a !== void 0 ? _a : [];
            /* migrate error -> exception */
            items.forEach((item) => {
                if (typeof item.error === 'string') {
                    item.exception = item.error;
                    delete item.error;
                }
            });
            this.setItems(items);
        }
        catch (_b) {
            this.setItems([]);
        }
    }
    postDeleteEvent(items) {
        if (this.channel) {
            const event = {
                type: EventType.Delete,
                items: items.map(item => toJS(item)),
            };
            this.channel.postMessage(event);
        }
    }
    handleDeleteEvent(event) {
        /* istanbul ignore next: because there's only one event type */
        if (event.type !== EventType.Delete) {
            return;
        }
        // Map item id to object for fast lookup
        const map = new Map(event.items.map(item => [item.id, item]));
        //  Discard deleted items
        const items = this.items.filter(item => !isEqual(item, map.get(item.id)));
        this.setItems(items);
        // If no more items, close channel; all saved items have been processed
        if (items.length === 0 && this.channel) {
            this.channel.close();
            delete this.channel;
        }
    }
    setItems(items) {
        this.items = [...items];
        localStorage.setItem(this.storageKey, JSON.stringify(this.items));
    }
    get storageKey() {
        return this.logConfig.storageKey;
    }
    get storageVersionKey() {
        return `${this.storageKey}Version`;
    }
    stringifyError(error) {
        const detail = error ? { message: error.message.toString(), stack: error.stack } : null;
        return JSON.stringify(detail);
    }
};
__decorate([
    observable,
    __metadata("design:type", Array)
], LogStorage.prototype, "items", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], LogStorage.prototype, "setItems", null);
LogStorage = __decorate([
    injectable(),
    __param(0, inject(LOG_CONFIG_TOKEN)),
    __metadata("design:paramtypes", [Object])
], LogStorage);
export { LogStorage };
function isEqual(a, b) {
    return b && Object.keys(a).every((key) => a[key] === b[key]);
}
export function lastId(items) {
    if (!items.length) {
        return 0;
    }
    const item = items[items.length - 1];
    return item.id;
}
//# sourceMappingURL=log-storage.js.map